import {
	DatasetCreationTypeEnum,
	DatasetStepEnum,
	type TDatasetMetaResponse,
	type TDatasetQuestion,
	type TDatasetSanitizationStep,
} from "../types/ledger.type";

import { getApiErrorMessage } from "./apiErrorUtils";

export const getLedgerApiErrorMessage = (
	error: unknown,
	fallback: string
): string => {
	const baseMessage = getApiErrorMessage(error, fallback);
	if (typeof error !== "object" || error === null) {
		return baseMessage;
	}

	const root = error as Record<string, unknown>;
	const response = root.response;
	const responseData =
		typeof response === "object" && response !== null
			? (response as Record<string, unknown>).data
			: undefined;
	const payload =
		typeof responseData === "object" &&
		responseData !== null
			? (responseData as Record<string, unknown>)
			: root;
	const details = [
		payload.error_code,
		payload.correlation_id,
	]
		.filter(
			(value): value is string | number =>
				typeof value === "string" ||
				typeof value === "number"
		)
		.map(String);

	return details.length > 0
		? `${baseMessage} (${details.join(" · ")})`
		: baseMessage;
};

export const allDatasetStepMapping: Record<
	DatasetStepEnum,
	Pick<
		TDatasetSanitizationStep,
		"path" | "name" | "nextStep"
	>
> = {
	[DatasetStepEnum.STEP_UG_STATIC_PRE_FILTER]: {
		path: "/ug-static-pre-filter",
		name: "Static Pre-filter",
		nextStep: DatasetStepEnum.STEP_UG_STATIC_FORMULAE,
	},
	[DatasetStepEnum.STEP_STATIC_UPLOAD]: {
		path: "/upload-static",
		name: "Static Upload",
		nextStep: DatasetStepEnum.STEP_STATIC_DATA_RANGE,
	},
	[DatasetStepEnum.STEP_STATIC_DATA_RANGE]: {
		path: "/static-data-range-selection",
		name: "Extract Static Data",
		nextStep: DatasetStepEnum.STEP_PERF_UPLOAD,
	},
	[DatasetStepEnum.STEP_STATIC_SANITIZATION]: {
		path: "/static-column-mapping",
		name: "Static Sanitize",
		nextStep: DatasetStepEnum.STEP_PERF_UPLOAD,
	},
	[DatasetStepEnum.STEP_PERF_UPLOAD]: {
		path: "/upload-performance-dataset",
		name: "Perf Data Upload",
		nextStep: DatasetStepEnum.STEP_PERF_DATA_RANGE,
	},
	[DatasetStepEnum.STEP_PERF_DATA_RANGE]: {
		path: "/performance-data-range-selection",
		name: "Extract Perf Data",
		nextStep: DatasetStepEnum.STEP_UG_STATIC_PRE_FILTER,
	},
	[DatasetStepEnum.STEP_PERF_SANITIZATION]: {
		path: "/performance-column-mapping",
		name: "Perf Sanitize",
		nextStep: DatasetStepEnum.STEP_DATASET_APPROVAL,
	},
	[DatasetStepEnum.STEP_UG_STATIC_FORMULAE]: {
		path: "/ug-static-formulae",
		name: "Static Formulae",
		nextStep: DatasetStepEnum.STEP_UG_STATIC_ENGINEERED,
	},
	[DatasetStepEnum.STEP_UG_STATIC_ENGINEERED]: {
		path: "/ug-static-engineered",
		name: "Static Engineered",
		nextStep: DatasetStepEnum.STEP_UG_STATIC_POST_FILTER,
	},
	[DatasetStepEnum.STEP_UG_STATIC_POST_FILTER]: {
		path: "/ug-static-post-filter",
		name: "Static Post-filter",
		nextStep: DatasetStepEnum.STEP_UG_PERF_PRE_FILTER,
	},
	[DatasetStepEnum.STEP_UG_PERF_PRE_FILTER]: {
		path: "/ug-perf-pre-filter",
		name: "Performance Pre-filter",
		nextStep: DatasetStepEnum.STEP_UG_PERF_FORMULAE,
	},
	[DatasetStepEnum.STEP_UG_PERF_FORMULAE]: {
		path: "/ug-perf-formulae",
		name: "Performance Formulae",
		nextStep: DatasetStepEnum.STEP_UG_PERF_POST_FILTER,
	},
	[DatasetStepEnum.STEP_UG_PERF_POST_FILTER]: {
		path: "/ug-perf-post-filter",
		name: "Performance Post-filter",
		nextStep: DatasetStepEnum.STEP_DATASET_APPROVAL,
	},
	[DatasetStepEnum.STEP_DATASET_APPROVAL]: {
		path: "/review-dataset",
		name: "Approve",
		nextStep: DatasetStepEnum.STEP_DATASET_COMPLETE,
	},
	[DatasetStepEnum.STEP_DATASET_COMPLETE]: {
		path: "/dataset-sanitization-complete",
		name: "Complete",
		nextStep: null,
	},
	[DatasetStepEnum.STEP_DATASET_SPECS]: {
		path: "/dataset-specifications",
		name: "Dataset Specs",
		nextStep: DatasetStepEnum.STEP_UG_STATIC_PRE_FILTER,
	},
};

export const getDatasetSanitizationSteps = (
	_method: DatasetCreationTypeEnum
): Record<
	DatasetStepEnum,
	Pick<
		TDatasetSanitizationStep,
		"path" | "name" | "nextStep"
	>
> => allDatasetStepMapping;

export const getDatasetFlowPath = (
	datasetId: string,
	meta: Pick<TDatasetMetaResponse, "dataset_type" | "current_step">
): string => {
	const steps = getDatasetSanitizationSteps(meta.dataset_type);
	if (!(meta.current_step in steps)) {
		throw new Error(`Unknown dataset step: ${meta.current_step}`);
	}
	return `/add-dataset/${encodeURIComponent(datasetId)}${steps[meta.current_step].path}`;
};
export const ledgerStatusOptions = [
	{
		label: "Any Status",
		value: "any",
		backgroundColor: "#FFFFFF",
		fontColor: "#000000",
	},
	{
		label: "Dataset Initialized",
		value: "not_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Dataset Specs Started",
		value: "dataset_specs_started",
		backgroundColor: "#FFF6E5",
		fontColor: "#FFA800",
	},
	{
		label: "Static Upload Started",
		value: "static_upload_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Static Data Range Started",
		value: "static_data_range_started",
		backgroundColor: "#FFF6E5",
		fontColor: "#FFA800",
	},
	{
		label: "Static Sanitization Started",
		value: "static_sanitization_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Static Pre-filter Started",
		value: "ug_static_pre_filter_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Static Formulae Started",
		value: "ug_static_formulae_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Engineered Columns Started",
		value: "ug_static_engineered_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Static Post-filter Started",
		value: "ug_static_post_filter_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Performance Upload Started",
		value: "perf_upload_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Performance Data Range Started",
		value: "perf_data_range_started",
		backgroundColor: "#FFF6E5",
		fontColor: "#FFA800",
	},
	{
		label: "Performance Sanitization Started",
		value: "perf_sanitization_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Performance Pre-filter Started",
		value: "ug_perf_pre_filter_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Performance Formulae Started",
		value: "ug_perf_formulae_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Performance Post-filter Started",
		value: "ug_perf_post_filter_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Approval Started",
		value: "approval_started",
		backgroundColor: "#FFF6E5",
		fontColor: "#FFA800",
	},
	{
		label: "Approval",
		value: "approval",
		backgroundColor: "#F4F3FF",
		fontColor: "#6938EF",
	},
	{
		label: "Approval Completed",
		value: "approval_completed",
		backgroundColor: "#EDFCF2",
		fontColor: "#16B364",
	},
	{
		label: "Dataset Complete",
		value: "complete",
		backgroundColor: "#CCFBEF",
		fontColor: "#0E9384",
	},
];

export const getLedgerStatus = (status: string) => {
	const matchingStatus = ledgerStatusOptions.find(
		(option) => option.value === status
	);
	if (matchingStatus) return matchingStatus;

	return {
		label: status
			? status
					.split("_")
					.map(
						(word) =>
							word.charAt(0).toUpperCase() + word.slice(1)
					)
					.join(" ")
			: "Status Unavailable",
		value: status,
		backgroundColor: "#F2F4F7",
		fontColor: "#475467",
	};
};

export const getDatasetCreationTypeLabel = (
	type?: DatasetCreationTypeEnum | string
): string => {
	switch (type) {
		case DatasetCreationTypeEnum.AssistedUserGuided:
			return "Assisted (User Guided)";
		case DatasetCreationTypeEnum.DatasetUploadUserGuided:
			return "Upload (User Guided)";
		default:
			return type
				? type
						.split("_")
						.map(
							(word) =>
								word.charAt(0).toUpperCase() + word.slice(1)
						)
						.join(" ")
				: "Unavailable";
	}
};

export const extractQuestionsDataset = (
	data: Array<any> | null
) => {
	if (!data) return [];
	const res: Array<TDatasetQuestion> = data.map(
		(value: any) => {
			const tempAnswer = value.answer;

			let masterOptions: Array<any> = [];

			if (
				value.answer_type_info.answer_type ===
				"option_selection"
			) {
				masterOptions = value.answer_type_info.options?.map(
					(option: any) => ({
						optionId: option.option_id,
						optionName: option.display_name,
					})
				);
			}

			const isEditable = true;

			return {
				question: value.question,
				questionId: value.question_id,
				answers: masterOptions,
				answerType: value.answer_type_info.answer_type,
				defaultAnswer: value.answer,
				validations: value.answer_type_info.constraints,
				isGenAIEnabled: value.is_genai_compatible,
				answer: tempAnswer,
				pythonValue: value.python_transformation_code,
				naturalTextValue: value.transform_description,
				categoryId: value.category_id,
				label: value.question,
				isProcessed: value.is_processed,
				isEditable,
			};
		}
	);
	return res;
};
